/**
 * UnassignedPanel — right-side accordion of unassigned jobs grouped by operator,
 * then site (Quick 260615-uf9 #4 / 260616-e14 #4). Also a drop target (id
 * `unassigned`) so a job dragged off a driver returns to the pool.
 *
 * Quick 260616-f4r:
 *   #1 — operator count badge is amber, site count badge is blue (visual hierarchy).
 *   #2 — "Collapse all / Expand all" toggle; accordion state persists for the
 *        session via sessionStorage.
 *   #4 — right-click / long-press a job chip → "Assign to…" quick-assign menu
 *        listing drivers (with availability dot), an alternative to drag-and-drop.
 */

import * as React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ChevronDown, ChevronRight, Inbox, PanelRightClose } from 'lucide-react';
import type { DispatchJob } from '../jobs/use-jobs';
import type { DriverSummary } from '../jobs/use-drivers';
import { JobChip } from './JobChip';
import { groupByOperatorSite, type DriverAvailability } from './board-utils';
import { QuietPhoneMark } from './QuietPhoneMark';
import { formatNumber } from '@lodex-pro/shared/number-format';

export interface UnassignedPanelProps {
  readonly jobs: readonly DispatchJob[];
  readonly drivers: readonly DriverSummary[];
  readonly availabilityById: ReadonlyMap<string, DriverAvailability>;
  /** OPEN-210 — drivers on shift whose phone has been quiet for over 30 minutes. */
  readonly quietById?: ReadonlySet<string>;
  readonly onSelect: (jobId: string) => void;
  readonly onAssign: (jobId: string, driverId: string) => void;
  /** #2 (260616-bd6) — board-level hide. CSS-hidden (display:none) when true so
   *  the driver grid fills full width while this panel keeps its state mounted. */
  readonly hidden?: boolean;
  /** #2 — hide this panel (button in the header). */
  readonly onHide?: () => void;
}

const OPS_KEY = 'lodex-board-unassigned-open-ops';
const SITES_KEY = 'lodex-board-unassigned-open-sites';

function loadRec(key: string): Record<string, boolean> {
  try {
    const raw = sessionStorage.getItem(key);
    return raw === null ? {} : (JSON.parse(raw) as Record<string, boolean>);
  } catch {
    return {};
  }
}
function saveRec(key: string, value: Record<string, boolean>): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* sessionStorage may be unavailable; non-fatal */
  }
}

/** A clearly-visible colored count badge (#1 — operator amber, site blue). */
function CountBadge({ count, tone }: { count: number; tone: 'blue' | 'amber' }): React.JSX.Element {
  const bg = tone === 'blue' ? 'var(--color-accent-blue)' : 'var(--color-warning)';
  return (
    <span
      style={{
        minWidth: '20px',
        height: '20px',
        padding: '0 6px',
        borderRadius: '10px',
        backgroundColor: bg,
        color: 'var(--color-on-accent)',
        fontSize: '12px',
        fontWeight: 700,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {formatNumber(count)}
    </span>
  );
}

const accordionBtn: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '6px',
  background: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: 'var(--radius-pill)',
  padding: '6px 8px',
  cursor: 'pointer',
  color: 'var(--color-text-primary)',
  width: '100%',
};

// OPEN-209 — 'offline' is off shift.
const AVAIL_COLOR: Record<DriverAvailability, string> = {
  available: 'var(--color-success)',
  busy: 'var(--color-warning)',
  offline: 'var(--color-danger)',
};

const AVAIL_LABEL: Record<DriverAvailability, string> = {
  available: 'Available',
  busy: 'Busy',
  offline: 'Off shift',
};

const NO_QUIET: ReadonlySet<string> = new Set();

export function UnassignedPanel({
  jobs,
  drivers,
  availabilityById,
  quietById = NO_QUIET,
  onSelect,
  onAssign,
  hidden = false,
  onHide,
}: UnassignedPanelProps): React.JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: 'unassigned', data: { driverId: null } });
  const operators = groupByOperatorSite(jobs);
  const [collapsed, setCollapsed] = React.useState(false);
  const [openOps, setOpenOps] = React.useState<Record<string, boolean>>(() => loadRec(OPS_KEY));
  const [openSites, setOpenSites] = React.useState<Record<string, boolean>>(() =>
    loadRec(SITES_KEY),
  );
  // #4 — quick-assign menu anchored at a viewport point.
  const [menu, setMenu] = React.useState<{ jobId: string; x: number; y: number } | null>(null);

  React.useEffect(() => saveRec(OPS_KEY, openOps), [openOps]);
  React.useEffect(() => saveRec(SITES_KEY, openSites), [openSites]);

  // Close the quick-assign menu on Escape.
  React.useEffect(() => {
    if (menu === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenu(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu]);

  const anyOpen = operators.some((op) => openOps[op.operator] ?? true);
  const toggleAll = (): void => {
    if (anyOpen) {
      // Collapse all → only operator headers show. #4 (260616-bd6) — also reset
      // sites to collapsed so a prior Expand-all doesn't leave site rows open.
      setOpenOps(Object.fromEntries(operators.map((op) => [op.operator, false])));
      setOpenSites({});
    } else {
      // Expand all opens OPERATORS only; site rows stay collapsed
      // (openSites[key] ?? false) until the dispatcher clicks one (#4).
      setOpenOps(Object.fromEntries(operators.map((op) => [op.operator, true])));
    }
  };

  const openMenu = React.useCallback((x: number, y: number, jobId: string): void => {
    setMenu({ jobId, x, y });
  }, []);

  return (
    <aside
      ref={setNodeRef}
      style={{
        width: '300px',
        flexShrink: 0,
        // #2 — CSS hide; the panel stays mounted so accordion/menu state survives.
        display: hidden ? 'none' : 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '10px',
        borderRadius: 'var(--radius-card)',
        border: isOver
          ? '2px solid var(--color-accent-blue)'
          : '1px solid var(--color-border-primary)',
        backgroundColor: 'var(--color-bg-secondary)',
        alignSelf: 'flex-start',
        maxHeight: '100%',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px',
        }}
      >
        <button
          type="button"
          onClick={(): void => setCollapsed((c) => !c)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--color-text-primary)',
            fontSize: '14px',
            fontWeight: 700,
            padding: 0,
          }}
        >
          <Inbox size={16} aria-hidden="true" />
          Unassigned
          <CountBadge count={jobs.length} tone="amber" />
          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        </button>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {!collapsed && operators.length > 0 && (
            <button
              type="button"
              onClick={toggleAll}
              style={{
                background: 'none',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: 'var(--radius-pill)',
                padding: '3px 8px',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
                fontSize: '11px',
                fontWeight: 600,
              }}
            >
              {anyOpen ? 'Collapse all' : 'Expand all'}
            </button>
          )}
          {/* #2 — hide the whole panel; the board shows a "show" button in its place. */}
          {onHide !== undefined && (
            <button
              type="button"
              onClick={onHide}
              aria-label="Hide unassigned panel"
              title="Hide unassigned panel"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'none',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: 'var(--radius-pill)',
                padding: '3px',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
              }}
            >
              <PanelRightClose size={15} />
            </button>
          )}
        </span>
      </div>

      {!collapsed &&
        (operators.length === 0 ? (
          <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
            No unassigned jobs.
          </span>
        ) : (
          operators.map((op) => {
            const opOpen = openOps[op.operator] ?? true;
            return (
              <div
                key={op.operator}
                style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
              >
                {/* Operator level */}
                <button
                  type="button"
                  onClick={(): void => setOpenOps((o) => ({ ...o, [op.operator]: !opOpen }))}
                  style={{ ...accordionBtn, fontSize: '13px', fontWeight: 700 }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                    {opOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span
                      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {op.operator}
                    </span>
                  </span>
                  <CountBadge count={op.count} tone="amber" />
                </button>

                {/* Site level (nested) */}
                {opOpen && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      paddingLeft: '10px',
                    }}
                  >
                    {op.sites.map((s) => {
                      const siteKey = `${op.operator}::${s.site}`;
                      // Quick 260616-fxg #2 — sites collapsed by default; the
                      // dispatcher clicks a site to reveal its jobs.
                      const siteOpen = openSites[siteKey] ?? false;
                      return (
                        <div
                          key={siteKey}
                          style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
                        >
                          <button
                            type="button"
                            onClick={(): void =>
                              setOpenSites((o) => ({ ...o, [siteKey]: !siteOpen }))
                            }
                            style={{ ...accordionBtn, fontSize: '12px', fontWeight: 600 }}
                          >
                            <span
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                minWidth: 0,
                              }}
                            >
                              {siteOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                              <span
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {s.site}
                              </span>
                            </span>
                            <CountBadge count={s.jobs.length} tone="blue" />
                          </button>
                          {siteOpen && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                paddingLeft: '8px',
                              }}
                            >
                              {s.jobs.map((job) => (
                                <JobChip
                                  key={job.id}
                                  job={job}
                                  draggable
                                  onSelect={onSelect}
                                  onContextMenu={openMenu}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ))}

      {/* #4 — quick-assign menu */}
      {menu !== null && (
        <QuickAssignMenu
          x={menu.x}
          y={menu.y}
          drivers={drivers}
          availabilityById={availabilityById}
          quietById={quietById}
          onClose={(): void => setMenu(null)}
          onPick={(driverId): void => {
            onAssign(menu.jobId, driverId);
            setMenu(null);
          }}
        />
      )}
    </aside>
  );
}

function QuickAssignMenu({
  x,
  y,
  drivers,
  availabilityById,
  quietById,
  onClose,
  onPick,
}: {
  x: number;
  y: number;
  drivers: readonly DriverSummary[];
  availabilityById: ReadonlyMap<string, DriverAvailability>;
  quietById: ReadonlySet<string>;
  onClose: () => void;
  onPick: (driverId: string) => void;
}): React.JSX.Element {
  const sorted = [...drivers].sort((a, b) => a.driverName.localeCompare(b.driverName));
  // Clamp so the menu stays on-screen.
  const left = Math.min(x, window.innerWidth - 230);
  const top = Math.min(y, window.innerHeight - 280);
  return (
    <>
      {/* Outside-click catcher */}
      <div
        onClick={onClose}
        onContextMenu={(e): void => {
          e.preventDefault();
          onClose();
        }}
        style={{ position: 'fixed', inset: 0, zIndex: 50 }}
      />
      <div
        role="menu"
        aria-label="Assign to driver"
        style={{
          position: 'fixed',
          left: `${left.toString()}px`,
          top: `${top.toString()}px`,
          zIndex: 51,
          width: '220px',
          maxHeight: '260px',
          overflowY: 'auto',
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border-primary)',
          borderRadius: '6px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          padding: '4px',
        }}
      >
        <div
          style={{
            fontSize: '11px',
            fontWeight: 700,
            color: 'var(--color-text-tertiary)',
            padding: '6px 8px',
          }}
        >
          Assign to…
        </div>
        {sorted.length === 0 ? (
          <div
            style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', padding: '6px 8px' }}
          >
            No drivers.
          </div>
        ) : (
          sorted.map((d) => {
            const avail = availabilityById.get(d.driverId) ?? 'available';
            return (
              <button
                key={d.driverId}
                type="button"
                role="menuitem"
                onClick={(): void => onPick(d.driverId)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  borderRadius: 'var(--radius-pill)',
                  padding: '7px 8px',
                  cursor: 'pointer',
                  color: 'var(--color-text-primary)',
                  fontSize: '13px',
                  textAlign: 'left',
                }}
                onMouseEnter={(e): void => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                }}
                onMouseLeave={(e): void => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span
                  aria-hidden="true"
                  title={AVAIL_LABEL[avail]}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: AVAIL_COLOR[avail],
                    flexShrink: 0,
                  }}
                />
                {quietById.has(d.driverId) && <QuietPhoneMark />}
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {d.driverName}
                </span>
              </button>
            );
          })
        )}
      </div>
    </>
  );
}
